import "./App.css";

import { useState, useEffect } from "react";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { getTimestamp } from "./storydat";
import * as u from "./utility";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { StoryItem } from "./StoryItem";

type TimelineChipsProps = {
  item: StoryItem;
  forRecentStories?: boolean;
};
export function TimelineChips({ item, forRecentStories }: TimelineChipsProps) {
  const [created, setCreated] = useState(-1);
  const [updated, setUpdated] = useState(-1);

  useEffect(() => {
    setCreated(daysElapsedSinceDate(getTimestamp(item)));
    let ts =
      !forRecentStories && item.st_updated_ts ? item.st_updated_ts : item.SK2;
    setUpdated(daysElapsedSince(ts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const bothChips = () => {
    const insignificant = Math.abs(created - updated) / created < 0.1;
    return (
      !forRecentStories &&
      elapsedDaysToString(created) !== elapsedDaysToString(updated) &&
      !insignificant
    );
  };

  const getChipStyle = (days: number, theme: any) => {
    if (forRecentStories) {
      return {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.12)' : '#f1f3f4',
        color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
        border: 'none',
      };
    }

    // Fresh (< 3 days) - Green
    if (days < 3) {
      return {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(129, 201, 149, 0.24)' : '#e6f4ea',
        color: theme.palette.mode === 'dark' ? '#81c995' : '#137333',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(129, 201, 149, 0.3)' : '#c6e7d0'}`,
      };
    }
    // Recent (< 7 days) - Yellow/Orange
    else if (days < 7) {
      return {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(248, 187, 134, 0.24)' : '#fef7e0',
        color: theme.palette.mode === 'dark' ? '#f8bb86' : '#b86a00',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(248, 187, 134, 0.3)' : '#f9e6b8'}`,
      };
    }
    // Older (< 30 days) - Pink/Red
    else if (days < 30) {
      return {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(242, 139, 130, 0.24)' : '#fce8e6',
        color: theme.palette.mode === 'dark' ? '#f28b82' : '#c5221f',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(242, 139, 130, 0.3)' : '#f6cec9'}`,
      };
    }
    // Default - Grey
    return {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.12)' : '#f1f3f4',
      color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
      border: 'none',
    };
  };

  const freshness = (updated: number) => {
    const days = elapsedDaysToString(updated);
    // if (days === "now") {
    //   return bothChips() ? "fresh" : "";
    // }
    // return  days + (bothChips() ? " fresh " : "")
    return days;
  };

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      {bothChips() && created >= 0 && (
        <Chip
          size="small"
          label={elapsedDaysToString(created) + " old"}
          sx={(theme) => ({
            height: '22px',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: '11px',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.08)' : '#f8f9fa',
            color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
            border: 'none',
            '& .MuiChip-label': {
              px: 1.25,
            },
          })}
        />
      )}

      {updated >= 0 && (
        <Chip
          size="small"
          label={freshness(updated)}
          sx={(theme) => ({
            height: '22px',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: '11px',
            ...getChipStyle(updated, theme),
            '& .MuiChip-label': {
              px: 1.25,
            },
          })}
        />
      )}
    </Stack>
  );
}

const elapsedDaysToString = (days: number) => {
  if (days === 0) {
    return "now";
  } else if (days < 30) {
    return `${days}d`;
  } else if (days < 365) {
    return `${Math.floor(days / 30)}m`;
  } else {
    return `${Math.floor(days / 365)}y`;
  }
};

const daysElapsedSinceDate = (date: string) => {
  const start = Date.parse(date);
  const now = Date.now();
  if (now < start) {
    return 0;
  }
  const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return days;
};

const daysElapsedSince = (ts: string) => {
  if (!ts || !ts.length) {
    return -1;
  }

  const start = u.timestampToDate(ts).getTime();
  const now = new Date().getTime();
  if (now < start) {
    return 0;
  }
  const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return days;
};

type StoryChipProps = {
  item: StoryItem;
  rowSelected?: boolean;
};

export function StoryChip({ item, rowSelected }: StoryChipProps) {
  return (
    <Box
      sx={(theme) => ({
        whiteSpace: "nowrap",
        height: '22px',
        px: 1.25,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        backgroundColor: rowSelected
          ? (theme.palette.mode === 'dark' ? 'rgba(138, 180, 248, 0.16)' : '#e8f0fe')
          : (theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.08)' : '#f1f3f4'),
        borderRadius: "11px",
        border: rowSelected
          ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(138, 180, 248, 0.3)' : '#c2daff'}`
          : 'none',
      })}
    >
      <Typography
        fontSize={10}
        fontWeight={600}
        sx={(theme) => ({
          color: theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368',
          letterSpacing: '0.3px',
        })}
      >
        STORY-
      </Typography>
      <Typography
        fontSize={13}
        fontWeight={700}
        sx={(theme) => ({
          color: rowSelected
            ? (theme.palette.mode === 'dark' ? '#8ab4f8' : '#1967d2')
            : (theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368'),
        })}
      >
        {Number(item.SK4)}
      </Typography>
    </Box>
  );
}

export const getRowColor = (item: StoryItem, isRowSelected?: boolean) => {
  const colors = {
    day: "#e8e8e8",
    two: "#f1f1f1",
    three: "#f1f1f1",
    week: "#f8f8f8",
    other: "var(--bg-main)",
  };

  if (isRowSelected) {
    return "var(--surface-selected)";
  }

  return colors.other;

  /*
  if (hasNotificationToday(item)) {
    return "#f5e0e0";
  }

  if (isPinned(item)) {
    return "#d9d4d0";
  }

  const ts = item.SK2;
  const start = u.timestampToDate(ts);
  const now = Date.now();
  if (now < start) {
    return colors.day;
  }

  const days = (now - start) / (1000 * 60 * 60 * 24);
  if (days < 1) {
    return colors.day;
  }

  if (days < 2) {
    return colors.two;
  }

  if (days < 4) {
    return colors.three;
  }

  if (days < 7) {
    return colors.week;
  }

  return colors.other;
  */
};
